import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import Card from '../shared/Card';
import Button from '../shared/Button';
import './GameGrid.css';

const GameGrid = ({ games, title, onGameClick, onExploreMore, showExploreButton = true }) => {
  const [imageLoadStates, setImageLoadStates] = useState({});

  const handleImageLoad = (gameId) => {
    setImageLoadStates(prev => ({ ...prev, [gameId]: 'loaded' }));
  };

  const handleImageError = (gameId) => {
    setImageLoadStates(prev => ({ ...prev, [gameId]: 'error' }));
  };

  const getImageSrc = (game) => {
    if (game.image?.[0]?.uri) {
      return game.image[0].uri;
    }
    
    const fallbackImages = {
      'MotoRace': '/images/MotoRace.png',
      'TheraStack': '/images/TheraStack.png',
      'ColorShot': '/images/ColorShot.png'
    };
    
    return fallbackImages[game.name] || '/images/placeholder.png';
  };

  const getGameTags = (game) => {
    if (game.legacyData?.tags) {
      return game.legacyData.tags.slice(0, 3);
    }
    return [
      game.legacyData?.category || 'Therapy',
      game.legacyData?.difficulty || 'Adaptive'
    ];
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 30,
      scale: 0.9
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="game-grid-section" aria-labelledby="game-grid-title">
      <div className="container">
        <motion.div
          className="game-grid-section-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 id="game-grid-title" className="game-grid-section-title">{title}</h2>
          <p className="game-grid-section-subtitle">
            Explore our therapeutic game collection designed for accessibility
          </p>
        </motion.div>

        <motion.div
          className="games-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {games.map((game, index) => {
            const gameId = game._id || `game-${index}`;
            const imageState = imageLoadStates[gameId];
            
            return (
              <motion.div
                key={gameId}
                variants={cardVariants}
                className="game-grid-item"
              >
                <Card
                  variant="dark"
                  hover={true}
                  glow={true}
                  className="game-card"
                  onClick={() => onGameClick?.(game)}
                  ariaLabel={`Explore ${game.name} - ${game.description?.substring(0, 50)}...`}
                >
                  <div className="game-image-container">
                    {imageState !== 'loaded' && imageState !== 'error' && (
                      <div className="image-loading" aria-hidden="true">
                        <div className="loading-spinner"></div>
                      </div>
                    )}
                    <img
                      src={getImageSrc(game)}
                      alt={`${game.name} - Therapeutic game interface`}
                      onLoad={() => handleImageLoad(gameId)}
                      onError={() => handleImageError(gameId)}
                      style={{
                        opacity: imageState === 'loaded' ? 1 : 0,
                        transition: 'opacity 0.3s ease'
                      }}
                    />
                    <div className="game-overlay" aria-hidden="true">
                      <div className="play-button">
                        <span className="play-icon">▶</span>
                      </div>
                    </div>
                  </div>

                  <div className="game-content">
                    <h3 className="game-title">{game.name}</h3>
                    <p className="game-description">
                      {game.description?.substring(0, 120)}...
                    </p>
                    
                    <div className="game-tags" role="list" aria-label="Game features">
                      {getGameTags(game).map((tag, tagIndex) => (
                        <span 
                          key={tagIndex} 
                          className="game-tag"
                          role="listitem"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    <div className="game-stats" aria-label="Game compatibility">
                      <div className="stat-item">
                        <span className="stat-icon">🎮</span>
                        <span className="stat-text">Adaptive Controls</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-icon">♿</span>
                        <span className="stat-text">Accessible</span>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>

        {showExploreButton && (
          <motion.div
            className="explore-more-container"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <Button
              variant="gradient"
              size="large"
              onClick={onExploreMore}
              ariaLabel="Explore all available games in our portal"
            >
              Explore All Games
            </Button>
          </motion.div>
        )}
      </div>
    </section>
  );
};

GameGrid.propTypes = {
  games: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    image: PropTypes.arrayOf(PropTypes.shape({
      uri: PropTypes.string
    })),
    legacyData: PropTypes.shape({
      category: PropTypes.string,
      difficulty: PropTypes.string,
      tags: PropTypes.arrayOf(PropTypes.string)
    })
  })).isRequired,
  title: PropTypes.string.isRequired,
  onGameClick: PropTypes.func,
  onExploreMore: PropTypes.func,
  showExploreButton: PropTypes.bool
};

export default GameGrid;